import { computed, ref } from 'vue'
import type { TaskFilterStatus, TaskRecord, TaskSummaryStats } from '../types'

const PRIORITY_WEIGHT: Record<TaskRecord['priority'], number> = {
  urgent: 4,
  high: 3,
  normal: 2,
  low: 1,
}

function isOverdue(task: TaskRecord) {
  if (!task.dueTime || task.status === 'done' || task.status === 'cancelled') {
    return false
  }
  return new Date(task.dueTime).getTime() < Date.now()
}

function effectiveStatus(task: TaskRecord): TaskRecord['status'] {
  if (
    isOverdue(task) &&
    task.status !== 'done' &&
    task.status !== 'cancelled'
  ) {
    return 'overdue'
  }
  return task.status
}

function isDoneToday(task: TaskRecord) {
  if (task.status !== 'done') return false
  const today = new Date()
  const created = new Date(task.createTime)
  return (
    created.getFullYear() === today.getFullYear() &&
    created.getMonth() === today.getMonth() &&
    created.getDate() === today.getDate()
  )
}

export function useTaskList(initialTasks: TaskRecord[]) {
  const tasks = ref<TaskRecord[]>(initialTasks.map((item) => ({ ...item })))
  const filterStatus = ref<TaskFilterStatus>('all')
  const keyword = ref('')
  const selectedId = ref<string | undefined>(initialTasks[0]?.id)

  const normalizedTasks = computed(() =>
    tasks.value.map((task) => ({
      ...task,
      status: effectiveStatus(task),
    }))
  )

  const summary = computed<TaskSummaryStats>(() => {
    const list = normalizedTasks.value
    return {
      pending: list.filter((item) => item.status === 'pending').length,
      processing: list.filter((item) => item.status === 'processing').length,
      overdue: list.filter((item) => item.status === 'overdue').length,
      doneToday: list.filter((item) => isDoneToday(item)).length,
    }
  })

  const filteredTasks = computed(() => {
    const kw = keyword.value.trim().toLowerCase()
    return normalizedTasks.value
      .filter((task) => {
        if (
          filterStatus.value !== 'all' &&
          task.status !== filterStatus.value
        ) {
          return false
        }
        if (!kw) return true
        return (
          task.title.toLowerCase().includes(kw) ||
          task.description?.toLowerCase().includes(kw) ||
          task.assignee.toLowerCase().includes(kw)
        )
      })
      .sort((a, b) => {
        const statusOrder = (status: TaskRecord['status']) => {
          if (status === 'overdue') return 0
          if (status === 'pending') return 1
          if (status === 'processing') return 2
          return 3
        }
        const statusDiff = statusOrder(a.status) - statusOrder(b.status)
        if (statusDiff !== 0) return statusDiff
        const priorityDiff =
          PRIORITY_WEIGHT[b.priority] - PRIORITY_WEIGHT[a.priority]
        if (priorityDiff !== 0) return priorityDiff
        return (
          new Date(b.createTime).getTime() - new Date(a.createTime).getTime()
        )
      })
  })

  const selectedTask = computed(
    () =>
      normalizedTasks.value.find((item) => item.id === selectedId.value) ?? null
  )

  function selectTask(id: string) {
    selectedId.value = id
  }

  function startTask(id: string) {
    const task = tasks.value.find((item) => item.id === id)
    if (!task || task.status === 'done' || task.status === 'cancelled') return
    task.status = 'processing'
  }

  function completeTask(id: string) {
    const task = tasks.value.find((item) => item.id === id)
    if (!task || task.status === 'done' || task.status === 'cancelled') return
    task.status = 'done'
  }

  function ignoreTask(id: string) {
    const task = tasks.value.find((item) => item.id === id)
    if (!task || task.status === 'done') return
    task.status = 'cancelled'
    if (selectedId.value === id) {
      selectedId.value = filteredTasks.value.find((item) => item.id !== id)?.id
    }
  }

  function replaceTasks(list: TaskRecord[]) {
    tasks.value = list.map((item) => ({ ...item }))
    if (!list.some((item) => item.id === selectedId.value)) {
      selectedId.value = list[0]?.id
    }
  }

  const remoteSummary = ref<TaskSummaryStats | null>(null)

  const displaySummary = computed<TaskSummaryStats>(() => {
    if (remoteSummary.value) {
      return remoteSummary.value
    }
    return summary.value
  })

  function setRemoteSummary(stats: TaskSummaryStats) {
    remoteSummary.value = stats
  }

  function clearRemoteSummary() {
    remoteSummary.value = null
  }

  return {
    tasks,
    filterStatus,
    keyword,
    selectedId,
    summary: displaySummary,
    filteredTasks,
    selectedTask,
    selectTask,
    startTask,
    completeTask,
    ignoreTask,
    replaceTasks,
    setRemoteSummary,
    clearRemoteSummary,
  }
}
